import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';

const StudentInfoModal = ({ 
  isOpen, 
  onClose, 
  userId, 
  title = "Student Information" 
}) => {
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [profilePhotoError, setProfilePhotoError] = useState(false);

  // Function to get semester display name - only if academic data is available
  const getSemesterDisplay = () => {
    if (!studentData) return null;
    
    // Only show semester if academic data is available
    if (!studentData.isAcademicDataAvailable) return null;
    
    // Check if we have semester data from the profile
    if (studentData.semester && studentData.semester.name) {
      return studentData.semester.name;
    }
    
    // Check if we have semester number from class mapping data
    if (studentData.student?.semester) {
      return studentData.student.semester;
    }
    
    // Check if we have semesterId and convert to display format
    if (studentData.student?.semesterId) {
      // If semesterId is a MongoDB ObjectId or complex object, don't convert to ordinal
      if (typeof studentData.student.semesterId === 'object') {
        // If it's an object with semesterNumber, use that
        if (studentData.student.semesterId.semesterNumber) {
          const semesterNumber = parseInt(studentData.student.semesterId.semesterNumber);
          if (!isNaN(semesterNumber)) {
            const suffix = ["th", "st", "nd", "rd"];
            const v = semesterNumber % 100;
            return `Semester ${semesterNumber}${suffix[(v - 20) % 10] || suffix[v] || suffix[0]}`;
          }
        }
        return 'Semester Information';
      }
      
      // If it's a simple string/number, try to parse it
      const semesterNumber = parseInt(studentData.student.semesterId);
      if (!isNaN(semesterNumber)) {
        const suffix = ["th", "st", "nd", "rd"];
        const v = semesterNumber % 100;
        return `Semester ${semesterNumber}${suffix[(v - 20) % 10] || suffix[v] || suffix[0]}`;
      }
      
      // If it's a string that doesn't parse to number, return as is
      return `Semester ${studentData.student.semesterId}`;
    }
    
    return null;
  };

  // Function to resolve profile photo URL
  const resolveProfilePhotoUrl = (profilePhotoUrl) => {
    if (!profilePhotoUrl) return null;
    
    // If it's already a full URL, return as is
    if (profilePhotoUrl.startsWith('http')) {
      return profilePhotoUrl;
    }
    
    // If it's a relative path starting with /api/, construct the full URL
    if (profilePhotoUrl.startsWith('/api/')) {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      // Remove any trailing slashes from baseUrl and ensure proper concatenation
      const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
      return `${cleanBaseUrl}${profilePhotoUrl}`;
    }
    
    // Return as is for other cases
    return profilePhotoUrl;
  };

  // Memoize the fetch function to avoid useEffect dependency issues
  const fetchStudentProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setProfilePhotoError(false);
      
      const subdomain = window.location.hostname.split('.')[0];
      
      console.log('🔄 Fetching student profile for modal...');
      const response = await api.post('/api/student/profile', {
        userId,
        subdomain
      });

      setStudentData(response.data.data);
      console.log('✅ Student profile loaded in modal:', response.data.data);
      
    } catch (err) {
      console.error('❌ Error fetching student profile:', err);
      setError(err.response?.data?.message || 'Failed to load student profile');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Fetch student profile when modal opens
  useEffect(() => {
    if (isOpen && userId) {
      fetchStudentProfile();
    }
  }, [isOpen, userId, fetchStudentProfile]);

  // Close modal and reset data
  const handleClose = () => {
    setStudentData(null);
    setError(null);
    setProfilePhotoError(false);
    onClose();
  };

  // Handle profile photo error
  const handleProfilePhotoError = () => {
    console.log('❌ Profile photo failed to load, using fallback');
    setProfilePhotoError(true);
  };

  // Don't render if not open
  if (!isOpen) return null;

  // Get resolved profile photo URL
  const profilePhotoUrl = studentData?.student?.profilePhotoUrl 
    ? resolveProfilePhotoUrl(studentData.student.profilePhotoUrl)
    : null;

  // Get semester display text
  const semesterDisplay = getSemesterDisplay();
  
  // Check if academic data is available
  const isAcademicDataAvailable = studentData?.isAcademicDataAvailable || false;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">{title}</h2>
            <button
              onClick={handleClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading student information...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="text-red-500 text-4xl mb-4">⚠️</div>
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={fetchStudentProfile}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {studentData && !loading && !error && (
            <div className="space-y-6">
              {/* Student Header Info */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6">
                  {/* Profile Photo */}
                  <div className="relative">
                    <div className="h-24 w-24 rounded-full bg-white border-4 border-white shadow-lg overflow-hidden">
                      {profilePhotoUrl && !profilePhotoError ? (
                        <img
                          src={profilePhotoUrl}
                          alt={`${studentData?.student?.firstName || 'Student'} ${studentData?.student?.lastName || ''}`}
                          className="h-24 w-24 rounded-full object-cover"
                          onError={handleProfilePhotoError}
                          onLoad={() => console.log('✅ Profile photo loaded successfully')}
                        />
                      ) : (
                        <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-3xl font-bold">
                          {(studentData?.student?.firstName?.charAt(0) || '') + 
                           (studentData?.student?.lastName?.charAt(0) || '') || 'S'}
                        </div>
                      )}
                    </div>
                    {/* Status Badge */}
                    <div className="absolute -bottom-2 -right-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        studentData?.student?.status === 'active' 
                          ? 'bg-green-500 text-white shadow-lg' 
                          : 'bg-red-500 text-white shadow-lg'
                      }`}>
                        {studentData?.student?.status?.toUpperCase() || 'Unknown'}
                      </span>
                    </div>
                  </div>

                  {/* Student Basic Info */}
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {(studentData?.student?.firstName && studentData?.student?.lastName) 
                        ? `${studentData.student.firstName} ${studentData.student.lastName}`
                        : studentData?.student?.name || 'Student Name Not Available'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center justify-center md:justify-start">
                          <span className="font-semibold text-gray-700 mr-2">🎓 USN:</span>
                          <span className="bg-white px-3 py-1 rounded-full border font-medium">
                            {studentData?.student?.USN || 'Not assigned'}
                        </span>
                      </div>
                      <div className="flex items-center justify-center md:justify-start">
                        <span className="font-semibold text-gray-700 mr-2">🎫 Admission No:</span>
                        <span className="bg-white px-3 py-1 rounded-full border font-mono font-medium">
                          {studentData?.student?.admissionNumber || 'Not assigned'}
                        </span>
                      </div>
                      <div className="flex items-center justify-center md:justify-start">
                        <span className="font-semibold text-gray-700 mr-2">📧 Email:</span>
                        <span className="text-blue-600 font-medium truncate">
                          {studentData?.student?.email || 'Not provided'}
                        </span>
                      </div>
                      <div className="flex items-center justify-center md:justify-start">
                        <span className="font-semibold text-gray-700 mr-2">📱 Phone:</span>
                        <span className="font-medium">
                          {studentData?.student?.studentPhoneNumber || 'Not provided'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Academic Information - Only show if academic data is available */}
              {isAcademicDataAvailable && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white border rounded-lg p-5 shadow-sm">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
                      <span className="text-blue-500 mr-3 text-xl">🎓</span>
                      Academic Details
                    </h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Admission Number:</span>
                        <span className="font-semibold text-blue-600">
                          {studentData?.student?.admissionNumber || 'Not assigned'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Class:</span>
                        <span className="font-semibold">
                          {studentData?.class?.className || studentData?.student?.className || 'Not assigned'}
                        </span>
                      </div>
                      {studentData?.student?.section && (
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-600 font-medium">Section:</span>
                          <span className="font-semibold">
                            {studentData?.student?.section || 'Not assigned'}
                          </span>
                        </div>
                      )}
                      {/* Show semester with proper display */}
                      {semesterDisplay && (
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-600 font-medium">Semester:</span>
                          <span className="font-semibold text-purple-600">
                            {semesterDisplay}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">USN:</span>
                        <span className="font-semibold">
                          {studentData?.student?.USN || 'Not assigned'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 font-medium">Academic Year:</span>
                        <span className="font-semibold text-green-600">
                          {studentData?.academicYear?.name || studentData?.student?.academicYear || 'Not assigned'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div className="bg-white border rounded-lg p-5 shadow-sm">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
                      <span className="text-green-500 mr-3 text-xl">👤</span>
                      Personal Details
                    </h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Gender:</span>
                        <span className="font-semibold">
                          {studentData?.student?.gender || 'Not provided'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Date of Birth:</span>
                        <span className="font-semibold">
                          {studentData?.student?.dateOfBirth || 'Not provided'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Blood Group:</span>
                        <span className="font-semibold">
                          {studentData?.student?.bloodGroup || 'Not provided'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Nationality:</span>
                        <span className="font-semibold">
                          {studentData?.student?.nationality || 'Not provided'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 font-medium">Category:</span>
                        <span className="font-semibold">
                          {studentData?.student?.category || 'Not provided'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* When academic data is not available, show only personal information */}
              {!isAcademicDataAvailable && (
                <div className="bg-white border rounded-lg p-5 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
                    <span className="text-green-500 mr-3 text-xl">👤</span>
                    Personal Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Admission Number:</span>
                        <span className="font-semibold text-blue-600">
                          {studentData?.student?.admissionNumber || 'Not assigned'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">USN:</span>
                        <span className="font-semibold">
                          {studentData?.student?.USN || 'Not assigned'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Gender:</span>
                        <span className="font-semibold">
                          {studentData?.student?.gender || 'Not provided'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Date of Birth:</span>
                        <span className="font-semibold">
                          {studentData?.student?.dateOfBirth || 'Not provided'}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Blood Group:</span>
                        <span className="font-semibold">
                          {studentData?.student?.bloodGroup || 'Not provided'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Nationality:</span>
                        <span className="font-semibold">
                          {studentData?.student?.nationality || 'Not provided'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 font-medium">Category:</span>
                        <span className="font-semibold">
                          {studentData?.student?.category || 'Not provided'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Information - Always show regardless of academic data */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border rounded-lg p-5 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
                    <span className="text-purple-500 mr-3 text-xl">📱</span>
                    Student Contact
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-start py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium w-20">Email:</span>
                      <span className="font-medium text-blue-600 break-all flex-1">
                        {studentData?.student?.email || 'Not provided'}
                      </span>
                    </div>
                    <div className="flex items-start py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium w-20">Phone:</span>
                      <span className="font-medium flex-1">
                        {studentData?.student?.studentPhoneNumber || 'Not provided'}
                      </span>
                    </div>
                    <div className="flex items-start py-2">
                      <span className="text-gray-600 font-medium w-20">Address:</span>
                      <span className="font-medium flex-1">
                        {studentData?.student?.address || 'Not provided'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-5 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
                    <span className="text-orange-500 mr-3 text-xl">👨‍👩‍👧‍👦</span>
                    Parent Contact
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-start py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium w-20">Name:</span>
                      <span className="font-medium flex-1">
                        {studentData?.student?.parentName || 'Not provided'}
                      </span>
                    </div>
                    <div className="flex items-start py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium w-20">Email:</span>
                      <span className="font-medium text-blue-600 break-all flex-1">
                        {studentData?.student?.parentEmail || 'Not provided'}
                      </span>
                    </div>
                    <div className="flex items-start py-2">
                      <span className="text-gray-600 font-medium w-20">Phone:</span>
                      <span className="font-medium flex-1">
                        {studentData?.student?.parentPhoneNumber || 'Not provided'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Institution Details - Always show regardless of academic data */}
              <div className="bg-white border rounded-lg p-5 shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
                  <span className="text-indigo-500 mr-3 text-xl">🏫</span>
                  Institution Details
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600 font-medium">Institution Name:</span>
                    <span className="font-semibold text-indigo-600">
                      {studentData?.tenant?.name || 'Not provided'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 font-medium">Subdomain:</span>
                    <span className="font-semibold">
                      {studentData?.tenant?.subdomain || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Remarks if available - Always show regardless of academic data */}
              {studentData?.student?.remarks && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center text-lg">
                    <span className="text-yellow-500 mr-3 text-xl">📝</span>
                    Remarks
                  </h4>
                  <p className="text-gray-700 bg-white p-3 rounded border">
                    {studentData.student.remarks}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-xl border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Last updated: {studentData?.student?.updatedAt ? 
                new Date(studentData.student.updatedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                }) 
                : 'Recently'}
            </div>
            <button
              onClick={handleClose}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentInfoModal;